/**
 * Embedded Signup da Meta — PORTE do componente canônico do zeepchat
 * (messaging/manager/apps/web/src/components/EmbeddedSignup.tsx), adaptado ao
 * stack do ERP:
 *  - appId/config ids vêm de GET /whatsapp/config (não de env do Vite);
 *  - o FB SDK é carregado sob demanda aqui (o manager o tinha no index.html);
 *  - feedback inline (o ERP não tem toaster);
 *  - o postMessage WA_EMBEDDED_SIGNUP FINISH (waba_id/phone_number_id) é
 *    CAPTURADO e enviado junto com o code no POST /whatsapp/signup — o legado
 *    só logava esses ids;
 *  - o seletor de fluxo vive aqui (Cloud API | Coexistence). Coexistence =
 *    featureType 'whatsapp_business_app_onboarding' + config id próprio; a
 *    dupla configId/featureType precisa casar com a Login Configuration do
 *    App Dashboard da Meta — mismatch = "Invalid configuration" sem erro útil.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Smartphone } from 'lucide-react';
import type { WhatsappChannel, WhatsappConfig, WhatsappSignupBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { WHATSAPP_KEY, errorMessage } from './whatsapp-queries';

// ── Facebook SDK (tipos mínimos do que usamos) ──────────────────────────────
interface FbLoginResponse {
  status?: string;
  authResponse?: { code?: string } | null;
  error?: { message?: string };
}

declare global {
  interface Window {
    FB?: {
      init: (config: { appId: string; version: string; xfbml?: boolean; cookie?: boolean }) => void;
      login: (
        callback: (response: FbLoginResponse) => void,
        options?: Record<string, unknown>
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

/**
 * Injeta o sdk.js uma única vez e resolve quando FB.init rodou. Promise em
 * escopo de módulo: re-montagens do componente reaproveitam o carregamento.
 */
let sdkPromise: Promise<void> | null = null;
function loadFbSdk(appId: string): Promise<void> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<void>((resolve, reject) => {
    if (window.FB) {
      window.FB.init({ appId, version: 'v20.0', cookie: true, xfbml: false });
      resolve();
      return;
    }
    window.fbAsyncInit = () => {
      window.FB?.init({ appId, version: 'v20.0', cookie: true, xfbml: false });
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error('Falha ao carregar o SDK do Facebook'));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

type Flow = WhatsappSignupBody['flow'];

const FLOW_OPTIONS: { value: Flow; label: string; hint: string }[] = [
  {
    value: 'cloud_api',
    label: 'Cloud API',
    hint: 'Número novo (ou dedicado) registrado direto na Cloud API da Meta — fluxo padrão.',
  },
  {
    value: 'coexistence',
    label: 'Coexistence (WhatsApp Business App)',
    hint: 'Já usa o aplicativo WhatsApp Business no celular? Conecte o MESMO número à Cloud API sem parar de usar o app.',
  },
];

type Status =
  | { kind: 'idle' }
  | { kind: 'sdk-error'; detail: string }
  | { kind: 'popup' } // FB.login aberto, aguardando o operador
  | { kind: 'saving' } // code capturado, POST /whatsapp/signup em voo
  | { kind: 'cancelled'; detail: string | null }
  | { kind: 'error'; detail: string }
  | { kind: 'success'; channels: WhatsappChannel[] };

export function EmbeddedSignup({ config }: { config: WhatsappConfig }) {
  const queryClient = useQueryClient();
  const [flow, setFlow] = useState<Flow>('cloud_api');
  const [sdkReady, setSdkReady] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  // Guard de HTTPS: o SDK da Meta se recusa a autenticar fora de https.
  const isSecure = window.location.protocol === 'https:';

  // ids observados no postMessage FINISH — chegam ANTES do callback do
  // FB.login (que traz o code); ref para o callback ler o valor mais recente.
  const observedIdsRef = useRef<{ wabaId?: string; phoneNumberId?: string }>({});

  useEffect(() => {
    if (!isSecure) return;
    let alive = true;
    loadFbSdk(config.fbAppId)
      .then(() => alive && setSdkReady(true))
      .catch((err: unknown) => {
        if (alive)
          setStatus({
            kind: 'sdk-error',
            detail: err instanceof Error ? err.message : 'Falha ao carregar o SDK do Facebook',
          });
      });
    return () => {
      alive = false;
    };
  }, [config.fbAppId, isSecure]);

  const signup = useMutation({
    mutationFn: async (body: WhatsappSignupBody) => {
      const res = await api<WhatsappChannel[]>('/whatsapp/signup', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (channels) => {
      setStatus({ kind: 'success', channels });
      void queryClient.invalidateQueries({ queryKey: [...WHATSAPP_KEY, 'channels'] });
    },
    onError: (err) => setStatus({ kind: 'error', detail: errorMessage(err) }),
  });
  const signupRef = useRef(signup.mutate);
  signupRef.current = signup.mutate;
  const flowRef = useRef(flow);
  flowRef.current = flow;

  // postMessage do popup: progresso/FINISH/CANCEL/ERROR do embedded signup.
  useEffect(() => {
    const monitorFbEvents = (event: MessageEvent) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return;
      }
      let data: {
        type?: string;
        event?: string;
        data?: {
          phone_number_id?: string;
          waba_id?: string;
          current_step?: string;
          error_message?: string;
        };
      };
      try {
        data = JSON.parse(event.data as string) as typeof data;
      } catch {
        return; // mensagens não-JSON do SDK — ignorar
      }
      if (data.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (data.event === 'FINISH') {
        // Guardar os ids: seguem para o backend junto com o code (o callback
        // do FB.login dispara logo em seguida com o code).
        observedIdsRef.current = {
          wabaId: data.data?.waba_id,
          phoneNumberId: data.data?.phone_number_id,
        };
      } else if (data.event === 'CANCEL') {
        setStatus({ kind: 'cancelled', detail: data.data?.current_step ?? null });
      } else if (data.event === 'ERROR') {
        setStatus({
          kind: 'error',
          detail: data.data?.error_message ?? 'Erro no embedded signup',
        });
      }
    };
    window.addEventListener('message', monitorFbEvents);
    return () => window.removeEventListener('message', monitorFbEvents);
  }, []);

  const fbLoginCallback = (response: FbLoginResponse) => {
    const code = response.authResponse?.code;
    if (code) {
      setStatus({ kind: 'saving' });
      const { wabaId, phoneNumberId } = observedIdsRef.current;
      signupRef.current({
        code,
        flow: flowRef.current,
        observedIds:
          wabaId || phoneNumberId ? { wabaId, phoneNumberId } : undefined,
      });
      return;
    }
    if (response.error) {
      setStatus({ kind: 'error', detail: response.error.message ?? 'Falha no login do Facebook' });
      return;
    }
    // Popup fechado sem completar — se o postMessage CANCEL já marcou o
    // estado, preservar o passo informado.
    setStatus((prev) => (prev.kind === 'cancelled' ? prev : { kind: 'cancelled', detail: null }));
  };

  const launchSignup = () => {
    if (!isSecure || !window.FB) return;
    observedIdsRef.current = {};
    setStatus({ kind: 'popup' });
    const configId = flow === 'coexistence' ? config.fbConfigIdCoexistence : config.fbConfigId;
    // featureType é o que alterna Cloud API (string vazia) ↔ coexistence.
    const featureType = flow === 'coexistence' ? 'whatsapp_business_app_onboarding' : '';
    try {
      window.FB.login(fbLoginCallback, {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType, sessionInfoVersion: '2' },
      });
    } catch (err) {
      setStatus({
        kind: 'error',
        detail: err instanceof Error ? err.message : 'Falha ao iniciar o login do Facebook',
      });
    }
  };

  const busy = status.kind === 'popup' || status.kind === 'saving';
  const canLaunch = isSecure && sdkReady && !busy;

  return (
    <div className="space-y-3">
      {/* Seletor de fluxo */}
      <fieldset className="space-y-1.5" disabled={busy}>
        <legend className="sr-only">Tipo de conexão</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {FLOW_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-2.5 text-sm transition-colors',
                flow === opt.value
                  ? 'border-primary bg-secondary'
                  : 'border-divider hover:border-border'
              )}
            >
              <span className="flex items-center gap-2 font-medium">
                <input
                  type="radio"
                  name="wa-flow"
                  value={opt.value}
                  checked={flow === opt.value}
                  onChange={() => setFlow(opt.value)}
                  className="accent-primary"
                />
                {opt.value === 'coexistence' ? <Smartphone className="size-4" /> : null}
                {opt.label}
              </span>
              <span className="text-xs text-muted-foreground">{opt.hint}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Guards */}
      {!isSecure ? (
        <p className="flex items-start gap-2 rounded-md border border-[#FFE58F] bg-[#FFFBE6] px-3 py-2 text-xs text-[#D48806]">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          O SDK do Facebook exige HTTPS — nesta origem ({window.location.protocol}//
          {window.location.host}) o Embedded Signup não abre. Em desenvolvimento local, use um túnel
          HTTPS (ex.: ngrok) ou acesse pelo domínio publicado.
        </p>
      ) : status.kind === 'sdk-error' ? (
        <p className="text-xs text-destructive" role="alert">
          {status.detail} — verifique bloqueadores de conteúdo e recarregue a página.
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={launchSignup} disabled={!canLaunch}>
          {busy ? <Spinner className="size-4 border-primary-foreground" /> : null}
          {status.kind === 'popup'
            ? 'Aguardando a Meta…'
            : status.kind === 'saving'
              ? 'Salvando número…'
              : 'Conectar com o Facebook'}
        </Button>
        {isSecure && !sdkReady && status.kind !== 'sdk-error' ? (
          <span className="text-xs text-muted-foreground">Carregando SDK do Facebook…</span>
        ) : null}
      </div>

      {/* Estados do fluxo */}
      {status.kind === 'cancelled' ? (
        <p className="text-xs text-muted-foreground" role="status">
          Conexão cancelada
          {status.detail ? ` (etapa: ${status.detail})` : ''} — nada foi alterado.
        </p>
      ) : null}
      {status.kind === 'error' ? (
        <p className="text-xs text-destructive" role="alert">
          {status.detail}
        </p>
      ) : null}
      {status.kind === 'success' ? (
        <div className="space-y-1.5 rounded-md border border-[#B7EB8F] bg-[#F6FFED] px-3 py-2">
          <p className="flex items-center gap-1.5 text-sm font-medium text-[#52C41A]">
            <CheckCircle2 className="size-4" />
            Número conectado com sucesso!
          </p>
          {status.channels.length > 0 ? (
            <ul className="space-y-0.5 text-xs text-foreground">
              {status.channels.map((c) => (
                <li key={c.id}>
                  {c.displayPhone}{' '}
                  <span className="text-muted-foreground">({c.phoneNumberId})</span>
                </li>
              ))}
            </ul>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Vincule cada número a uma mesa na tabela abaixo para começar a receber conversas.
          </p>
        </div>
      ) : null}
    </div>
  );
}
